import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Boxes, RefreshCw, Search, Check } from 'lucide-react';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';

const SOURCE = {
  manual: { label: 'Definido na app', cls: 'bg-blue-100 text-blue-800' },
  sage_confirmed: { label: 'Confirmado pelo SAGE', cls: 'bg-emerald-100 text-emerald-800' },
  unit_table: { label: 'Sugerido pelo nome — por confirmar', cls: 'bg-amber-100 text-amber-800' },
  unknown: { label: 'Por definir', cls: 'bg-red-100 text-red-700' },
};

const FactorCell = ({ row, onSaved }) => {
  const [value, setValue] = useState(row.factor ?? row.suggested_factor ?? '');
  const [saving, setSaving] = useState(false);
  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/purchase-units/articles/${encodeURIComponent(row.sku)}`, { factor: value ? Number(value) : null });
      onSaved(res.data);
      toast.success(`${row.sku}: fator guardado`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };
  return (
    <div className="flex items-center gap-1">
      <Input type="number" min="1" value={value} onChange={(e) => setValue(e.target.value)} className="w-20 h-8" data-testid={`factor-input-${row.sku}`} />
      <Button size="icon" variant="outline" className="h-8 w-8" onClick={save} disabled={saving} title="Guardar / confirmar este fator" data-testid={`factor-save-${row.sku}`}><Check className="w-4 h-4" /></Button>
    </div>
  );
};

const RefreshStatus = ({ refresh }) => {
  if (!refresh?.status) return <span>Ainda não foram lidas as unidades dos artigos no SAGE.</span>;
  if (refresh.status === 'running') return <span>A ler artigos do SAGE… página {refresh.pages} ({refresh.updated} atualizados)</span>;
  if (refresh.status === 'failed') return <span className="text-red-600">Leitura falhou: {refresh.error}</span>;
  return <span>Última leitura: {new Date(refresh.finished_at).toLocaleString('pt-PT')} · {refresh.updated} artigos atualizados</span>;
};

export default function PurchaseUnits() {
  const [data, setData] = useState({ articles: [], refresh: {} });
  const [search, setSearch] = useState('');
  const [onlyPending, setOnlyPending] = useState(false);

  const load = useCallback(async () => {
    const res = await axios.get(`${API}/purchase-units/articles`, { params: { search, only_pending: onlyPending } });
    setData(res.data);
  }, [search, onlyPending]);

  useEffect(() => { load().catch(() => toast.error('Erro ao carregar')); }, [load]);
  useEffect(() => {
    if (data.refresh?.status !== 'running') return undefined;
    const t = setInterval(() => load().catch(() => {}), 5000);
    return () => clearInterval(t);
  }, [data.refresh?.status, load]);

  const refresh = async () => {
    if (!window.confirm('Ler a unidade de compra de todos os artigos no SAGE? Demora cerca de 2 minutos (só leitura).')) return;
    const res = await axios.post(`${API}/purchase-units/refresh`);
    toast.info(res.data.started ? 'Leitura de artigos iniciada' : res.data.message);
    load();
  };
  const onSaved = (r) => setData((d) => ({ ...d, articles: d.articles.map((a) => (a.sku === r.sku ? { ...a, ...r } : a)) }));
  const suggested = data.articles.filter((a) => a.source === 'unit_table' && a.suggested_factor);
  const confirmAll = async () => {
    if (!window.confirm(`Confirmar o fator sugerido pelo nome da unidade para ${suggested.length} artigos? Confirme antes que coincide com a ficha do artigo no SAGE.`)) return;
    const res = await axios.post(`${API}/purchase-units/articles/confirm-suggested`, { skus: suggested.map((a) => a.sku) });
    toast.success(`${res.data.confirmed} artigos confirmados`);
    load();
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl space-y-6" data-testid="purchase-units-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900 flex items-center gap-2"><Boxes className="w-6 h-6" /> Unidades de Compra</h1>
          <p className="text-sm text-slate-500 max-w-3xl">Artigos que o fornecedor vende em caixa. As previsões e o stock ficam em unidades; as encomendas são arredondadas para cima a caixas completas e seguem para o SAGE e para o fornecedor em caixas.</p>
        </div>
        <Button onClick={refresh} disabled={data.refresh?.status === 'running'} data-testid="purchase-units-refresh-btn">
          <RefreshCw className={`w-4 h-4 mr-2 ${data.refresh?.status === 'running' ? 'animate-spin' : ''}`} />Ler unidades do SAGE
        </Button>
      </div>
      <p className="text-xs text-slate-500 -mt-3" data-testid="purchase-units-refresh-status"><RefreshStatus refresh={data.refresh} /></p>
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900" data-testid="purchase-units-info">
        O SAGE não envia o "Fator grupagem" pela API. O número no nome da unidade (C10 → 10) é só uma <b>sugestão</b> e não é usado até ser confirmado (✓ na linha, ou confirmação em bloco). Na primeira encomenda criada pela app, o fator é verificado com o que o SAGE aplicou e fica <b>confirmado pelo SAGE</b>. Enquanto não houver fator confirmado, o artigo é encomendado em unidades.
      </div>
      <div className="flex flex-wrap items-center gap-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Pesquisar artigo…" className="pl-8 w-72" data-testid="purchase-units-search" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          <Switch checked={onlyPending} onCheckedChange={setOnlyPending} data-testid="purchase-units-only-pending" /> Só por confirmar
        </label>
        <span className="text-sm text-slate-500" data-testid="purchase-units-total">{data.total || 0} artigos</span>
        {suggested.length > 0 && (
          <Button variant="outline" size="sm" onClick={confirmAll} data-testid="purchase-units-confirm-all-btn">
            Confirmar sugestões visíveis ({suggested.length})
          </Button>
        )}
      </div>
      <div className="rounded-lg border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-sm" data-testid="purchase-units-table">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase">
            <tr><th className="text-left p-3">Artigo</th><th className="text-left p-3">Fornecedor</th><th className="text-left p-3">Unidade compra</th><th className="text-left p-3">Fator (UN por unidade)</th><th className="text-left p-3">Origem</th></tr>
          </thead>
          <tbody>
            {data.articles.map((r) => (
              <tr key={r.sku} className="border-t" data-testid={`purchase-unit-row-${r.sku}`}>
                <td className="p-3"><div className="font-mono text-xs">{r.sku}</div><div className="text-slate-600">{r.name}</div></td>
                <td className="p-3 text-slate-600">{r.supplier || '—'}</td>
                <td className="p-3"><span className="font-mono">{r.unit}</span> <span className="text-slate-400 text-xs">{r.description}</span></td>
                <td className="p-3"><FactorCell row={r} onSaved={onSaved} /></td>
                <td className="p-3"><Badge className={(SOURCE[r.source] || SOURCE.unknown).cls}>{(SOURCE[r.source] || SOURCE.unknown).label}</Badge></td>
              </tr>
            ))}
            {!data.articles.length && (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400" data-testid="purchase-units-empty">Nenhum artigo com unidade de compra diferente de UN. Use "Ler unidades do SAGE".</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
